import type { GetServerSideProps } from "next"
import type { NextPageWithLayout } from "common/types/page"
import { Layout as NewslettersPageLayout } from "pages/newsletters/layout"
import { AppProps, getAppServerSideProps } from "common/lib/getAppServerSideProps"
import { useRememberRegion } from "common/hooks/data/useRememberRegion"
import { MetaAttributes } from "common/components/MetaAttributes"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import { gql } from "common/lib/gql"
import { tcFrontendToken } from "../../k8sSecrets.server"
import { fetchGraphqlQuery } from "common/lib/fetchGraphqlQuery"
import { Group, NewslettersPageQuery, PageTypeEnum } from "common/types/graphql"
import { tracer } from "common/lib/tracer"
import { useNewsletterGroups } from "modules/newsletters/hooks/useNewsletterGroups"
import { useRegionConfig } from "modules/i18n/hooks/useRegionConfig"
import { useViewer } from "common/hooks/data/useViewer"
import { trackPageViewed } from "common/lib/tcEventCollector"
import { useEffect } from "react"
import { NewslettersMain } from "modules/newsletters/components/NewslettersMain"
import { getUtmAttribution } from "common/lib/utm/getUtmAttribution"
import { getReferrer } from "common/lib/utm/getReferrer"

export interface NewslettersPageProps extends AppProps {
  groups: Group[]
}

const QUERY = gql`
  query newslettersPage($regionCode: String!) {
    newslettersPage(regionCode: $regionCode) {
      cacheKey
      groups {
        name
        groupItems {
          id
          name
          promoted
          frequency
          description
          pitch
          heroImageUrl
          thumbnailImageUrl
        }
      }
    }
  }
`

export const getServerSideProps: GetServerSideProps<NewslettersPageProps> = async ({
  query,
  res,
  req,
}) => {
  if (req.method !== "GET" && req.method !== "HEAD") return { notFound: true }

  const appProps = await getAppServerSideProps({ query, res, req })
  const { regionCode } = appProps

  const headers: { Authorization: string; User?: string } = {
    Authorization: `Bearer ${tcFrontendToken()}`,
  }

  const { newslettersPage } = await fetchGraphqlQuery<NewslettersPageQuery>(
    QUERY,
    headers,
    { regionCode },
    tracer,
  )

  res.setHeader("Cache-Control", "no-store")
  res.setHeader("Surrogate-Control", "max-age=14400") // Newsletter sub page expires in 4 hours
  res.setHeader("Surrogate-Key", newslettersPage?.cacheKey || "")

  return {
    props: { ...appProps, groups: newslettersPage?.groups || [], pageType: PageTypeEnum.Resource },
  }
}

const NewslettersPage: NextPageWithLayout<NewslettersPageProps> = () => {
  useRememberRegion()
  const {
    social_media: { facebook_url: facebookURL },
  } = useRegionConfig()
  const t = useTranslations("newsletters")
  const { promotedNewsletter } = useNewsletterGroups()
  const { isSignedIn, loaded } = useViewer()

  useEffect(() => {
    if (loaded) {
      const attribution = getUtmAttribution()
      const referrer = getReferrer()
      trackPageViewed({
        urlPath: window.location.pathname,
        userLoggedIn: isSignedIn,
        category: "newsletters",
        ...(attribution && { attribution }),
        ...(referrer && { referrer }),
      })
    }
    // We intentionally only depend on `loaded` to track once when viewer data finishes loading.
    // `isSignedIn` will have the correct value at that point and shouldn't change afterward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded])

  return (
    <>
      <MetaAttributes
        title={t("meta.title")}
        description={t("meta.description")}
        ogTitle={t("meta.title")}
        ogDescription={t("meta.description")}
        articlePublisher={facebookURL}
        image={promotedNewsletter?.heroImageUrl}
      />
      <NewslettersMain />
    </>
  )
}

NewslettersPage.getLayout = (page) => <NewslettersPageLayout>{page}</NewslettersPageLayout>

export default NewslettersPage
